import * as React from "react";
import { useContext } from "react";
import { DeviceMetering } from "../../../../../../../components/device/device_metering";
import { DeviceMeteringLive } from "../../../../../../../components/device/device_metering_live";
import VerifyCheckbox from "./verify_checkbox";
import { CommissioningStateContext } from "../../hooks/use_commissioning_state_changes";
import { Button, Stack, Typography } from "@mui/material";
import { isComponentDisabledForUpdates } from "../../../../../../login/login_utils";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { useModal } from "../../../../../../../context/modal_provider";
import { useUserPrivilegeKeyPermissionsMap } from "../../../../../../../hooks/use_backend";
import { useSiteData } from "../../../../../../../hooks/use_site";
import { LIVE_DATA_WARNINGS } from "../../enum/live_data_warnings";
import { COMMISSIONING_PHASE_KEYS } from "../../enum/commissioning_phase_keys";
import { isLockedByVerification } from "../../enum/commissioning_required_action";
import { CollapsableCard } from "../../../../../../../components/general/collapsable_card";
import { GPCCard } from "../../../../../../../components/general_tab/gpc_card";
import QuickOverrideButton from "./quick_override_buttons";

export const VerifyDeviceLiveData = ({ requiredPrivilege }) => {
  const { commissioningState, deviceStepMutation, isDeviceStepMutating, dispatchCommissioningStateChanges } =
    useContext(CommissioningStateContext);
  const { data: siteData, error, isLoading } = useSiteData();
  const { openOverrideModal } = useModal();
  const permissionMap = useUserPrivilegeKeyPermissionsMap();
  // add this to enums instead...
  const gridMeterValidationWarning = "Verify the grid meter first";
  const gridMeterBlocksProgress =
    !commissioningState.selectedDevice.device_commissioned &&
    commissioningState.selectedPhase.phase_key === COMMISSIONING_PHASE_KEYS.BATTERIES &&
    !commissioningState.validationState.grid_meter_completed;
  const verificationLocksTheCurrentState = isLockedByVerification(
    commissioningState.selectedDevice.verification_required_action,
  );
  const userNotAllowedToUpdate = isComponentDisabledForUpdates(permissionMap, requiredPrivilege.getPrivilegeKey());
  const meteringFinishedFetching =
    commissioningState.meteringLiveDataQueryRan && commissioningState.meteringDataQueryRan;

  const deviceId = commissioningState.selectedDevice.device_id;

  const checkboxIsChecked = commissioningState.selectedDevice.is_completed;
  const deviceIsCommissioned = commissioningState.selectedDevice.device_commissioned;
  const isSolar = commissioningState.selectedPhase.phase_key === COMMISSIONING_PHASE_KEYS.SOLAR_INVERTERS;
  const isBattery = commissioningState.selectedPhase.phase_key === COMMISSIONING_PHASE_KEYS.BATTERIES;

  const isDisabled =
    gridMeterBlocksProgress || verificationLocksTheCurrentState || deviceIsCommissioned || userNotAllowedToUpdate;
  // when disabled because of grid meter still allow to uncheck the checkbox.
  const checkboxMutationDisabled =
    (!checkboxIsChecked && !meteringFinishedFetching) || // When meter data has not finished fetching and checkbox is unchecked
    (!checkboxIsChecked && gridMeterBlocksProgress) || // When meter data has not finished fetching and grid meter is unfinished
    verificationLocksTheCurrentState || // Verification is in a state that blocks updates here ('sturing/admin approval')
    deviceIsCommissioned || // When device is already commissioned
    userNotAllowedToUpdate; // When user does not have the rights

  function GeneralInfoLine({ name, value }) {
    return (
      <Stack direction="row" sx={{ width: "100%" }}>
        <Typography
          variant="body"
          sx={{
            minWidth: "130px",
            display: "flex",
            userSelect: "none",
            fontWeight: "bold",
            color: "#999",
          }}
        >
          {name}:&nbsp;
        </Typography>
        <Typography variant="body" sx={{ wordWrap: "break-word", fontWeight: "bold" }}>
          {value}
        </Typography>
      </Stack>
    );
  }

  function showAdditionalGridMeteringLiveData(commissioningState) {
    return (
      (commissioningState.selectedPhaseKey === COMMISSIONING_PHASE_KEYS.BATTERIES ||
        commissioningState.selectedPhaseKey === COMMISSIONING_PHASE_KEYS.SOLAR_INVERTERS) &&
      commissioningState.validationState.has_grid_meter &&
      commissioningState.validationState.grid_meter != null
    );
  }

  return (
    <div style={{ padding: "10px" }}>
      <div>
        <QuickOverrideButton
          isDisabled={isDisabled}
          site_id={siteData.site_id}
          device_id={deviceId}
          phase_key={commissioningState.selectedPhaseKey}
        />
      </div>
      {isBattery && (
        <div style={{ marginBottom: "10px" }}>
          <GeneralInfoLine
            key={"capacity"}
            name={"Capacity"}
            value={commissioningState.selectedDevice?.raw?.capacity}
          />
          <GeneralInfoLine
            key={"charge rate"}
            name={"Charge rate"}
            value={commissioningState.selectedDevice?.raw?.charge_rate}
          />
        </div>
      )}
      <div style={{ marginBottom: "10px" }}>
        <CollapsableCard startCollapsed={checkboxIsChecked} title={"Live metering"} sx={{ mt: 2 }}>
          {!commissioningState.stepValidationWarnings.includes(LIVE_DATA_WARNINGS.DEVICE_LIVE_METERING_COMPONENT) ? (
            <DeviceMeteringLive
              device={commissioningState.selectedDevice}
              isGrid={commissioningState.selectedPhaseKey === COMMISSIONING_PHASE_KEYS.GRID_METER}
              dispatchCommissioningStateChanges={dispatchCommissioningStateChanges}
            />
          ) : isDisabled ? null : (
            <Typography variant="body2" color="error" fontWeight="bold">
              {LIVE_DATA_WARNINGS.DEVICE_LIVE_METERING_COMPONENT}
            </Typography>
          )}
        </CollapsableCard>
      </div>
      {showAdditionalGridMeteringLiveData(commissioningState) ? (
        <div style={{ marginBottom: "10px" }}>
          <CollapsableCard startCollapsed={checkboxIsChecked} title={"Grid live metering"} sx={{ mt: 2 }}>
            {!commissioningState.stepValidationWarnings.includes(LIVE_DATA_WARNINGS.DEVICE_LIVE_METERING_COMPONENT) ? (
              <DeviceMeteringLive device={commissioningState.validationState.grid_meter} isGrid={true} />
            ) : isDisabled ? null : (
              <Typography variant="body2" color="error" fontWeight="bold">
                {LIVE_DATA_WARNINGS.DEVICE_LIVE_METERING_COMPONENT}
              </Typography>
            )}
          </CollapsableCard>
        </div>
      ) : null}
      <div style={{ marginBottom: "10px" }}>
        <CollapsableCard startCollapsed={checkboxIsChecked} title={"Historical metering"} sx={{ mt: 2 }}>
          {!commissioningState.stepValidationWarnings.includes(LIVE_DATA_WARNINGS.DEVICE_METERING_COMPONENT) ? (
            <DeviceMetering
              device={commissioningState.selectedDevice}
              dispatchCommissioningStateChanges={dispatchCommissioningStateChanges}
              timeDelta={3}
            />
          ) : isDisabled ? null : (
            <Typography variant="body2" color="error" fontWeight="bold">
              {LIVE_DATA_WARNINGS.DEVICE_METERING_COMPONENT}
            </Typography>
          )}
        </CollapsableCard>
      </div>
      {isSolar && (
        <div style={{ marginBottom: "10px" }}>
          <GPCCard
            startCollapsed={checkboxIsChecked}
            permissionMap={permissionMap}
            isEditable={false}
            required_privilege={requiredPrivilege}
            sx={{ mt: 2 }}
          />
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <VerifyCheckbox
          commissioningState={commissioningState}
          mutation={deviceStepMutation}
          isMutating={isDeviceStepMutating}
          // Unchecking is still allowed...
          isDisabled={checkboxMutationDisabled}
        />
        <Button
          onClick={() => openOverrideModal({ siteData })}
          disabled={isDisabled || isDeviceStepMutating}
          endIcon={<AutorenewIcon fontSize="small" />}
        >
          Manual override
        </Button>
      </div>
      <div>
        {!checkboxIsChecked && gridMeterBlocksProgress && (
          // Unchecking is still allowed, > only show gridMeterValidationWarning when chevkbox is unChecked
          <Typography variant="body2" color="error" fontWeight="bold">
            {gridMeterValidationWarning}
          </Typography>
        )}
      </div>
    </div>
  );
};
